import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentProps, ReactElement } from 'react';
import React, { Children, cloneElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons/faChevronRight';

export interface CarouselProps extends ComponentProps<'div'> {
  indicators?: boolean;
  slide?: boolean;
  slideInterval?: number;
}

export const Carousel = ({
  children,
  slide = true,
  slideInterval,
  className,
  ...props
}: CarouselProps): JSX.Element => {
  const carouselContainer = useRef<HTMLDivElement>(null);
  const [activeItem, setActiveItem] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const didMountRef = useRef(false);

  const items = useMemo(
    () =>
      Children.map(children as ReactElement[], (child: ReactElement) =>
        cloneElement(child, {
          className: `absolute top-1/2 left-1/2 block w-full -translate-x-1/2 -translate-y-1/2 ${child.props.className}`,
        }),
      ),
    [children],
  );

  const navigateTo = useCallback(
    (item: number): (() => void) => () => {
      if (!items) return;
      const nextItem = (item + items.length) % items.length;
      if (carouselContainer.current) {
        carouselContainer.current.scrollLeft = carouselContainer.current.clientWidth * nextItem;
      }
      setActiveItem(nextItem);
    },
    [items],
  );

  useEffect(() => {
    if (carouselContainer.current && !isDragging && carouselContainer.current.scrollLeft !== 0) {
      setActiveItem(Math.round(carouselContainer.current.scrollLeft / carouselContainer.current.clientWidth));
    }
  }, [isDragging]);

  useEffect(() => {
    if (slide && !isHovering) {
      const intervalId = setInterval(() => !isDragging && navigateTo(activeItem + 1)(), slideInterval ?? 5000);

      return (): void => clearInterval(intervalId);
    }
    return (): void => { };
  }, [activeItem, isDragging, navigateTo, slide, slideInterval, isHovering]);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
    }
  }, [activeItem]);

  const handleDragging = (dragging: boolean): (() => void) => (): void => setIsDragging(dragging);
  const setHoveringTrue = useCallback(() => setIsHovering(true), [setIsHovering]);
  const setHoveringFalse = useCallback(() => setIsHovering(false), [setIsHovering]);

  return (
    <div
      className={`relative h-full w-full ${className}`}
      onMouseEnter={setHoveringTrue}
      onMouseLeave={setHoveringFalse}
      onTouchStart={setHoveringTrue}
      onTouchEnd={setHoveringFalse}
      {...props}
    >
      <ScrollContainer
        draggingClassName='cursor-grab'
        className={`flex h-full snap-mandatory overflow-y-hidden overflow-x-scroll scroll-smooth space-x-2 ${(!isDragging) && 'snap-x'}`}
        innerRef={carouselContainer}
        onEndScroll={handleDragging(false)}
        onStartScroll={handleDragging(true)}
        vertical={false}
        horizontal
      >
        {items?.map((item, index) => (
          <div
            className='w-full transform cursor-default hover:cursor-grab snap-center first:pl-[18px] last:pr-[18px]'
            key={index} // eslint-disable-line react/no-array-index-key
            data-active={activeItem === index}
          >
            {item}
          </div>
        ))}
      </ScrollContainer>
      {items && (
        <>
          <div className='absolute top-0 left-0 hidden md:flex h-full items-center justify-center px-4 focus:outline-none text-white'>
            <button
              className='pl-2'
              onClick={navigateTo(activeItem - 1)}
              type='button'
              aria-label='Previous slide'
            >
              <FontAwesomeIcon icon={faChevronLeft} size='2xl' />
            </button>
          </div>
          <div className='absolute top-0 right-0 hidden md:flex h-full items-center justify-center px-4 focus:outline-none text-white'>
            <button
              className='pr-2'
              onClick={navigateTo(activeItem + 1)}
              type='button'
              aria-label='Next slide'
            >
              <FontAwesomeIcon icon={faChevronRight} size='2xl' />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
